import { getParentAttribute } from "@/utils/getParent";
import { useEventListener, useMemoizedFn, useMount } from "ahooks";
import { useRef, useState } from "react";
import {
  EVENT_POINTER_CANCEL,
  EVENT_POINTER_DOWN,
  EVENT_POINTER_MOVE,
  EVENT_POINTER_UP,
} from "./constant";

/**移动动作方向 */
export enum MoveAction {
  top,
  right,
  bottom,
  left,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  /**整体移动 */
  whole,
  /**重置默认 */
  reset,
  /**重新框选 */
  resize,
}

export interface IRectCoordinate {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

type TPointerEvent = PointerEvent | MouseEvent | TouchEvent;

export function useEdgeCrop({
  defaultCoordinate,
  onResizing,
  onResizeEnd,
}: {
  defaultCoordinate?: IRectCoordinate;
  onResizing?: (coordinate: IRectCoordinate) => void;
  onResizeEnd?: (coordinate: IRectCoordinate) => void;
} = {}) {
  //左上角与右下角顶点的坐标， 相对容器
  const [coordinate, setCoordinate] = useState<IRectCoordinate>(
    defaultCoordinate || {
      top: 10,
      left: 10,
      bottom: 100,
      right: 100,
    }
  );

  // 裁剪边界限制范围
  const edgeLimitCoordinate = useRef<IRectCoordinate>(null);
  /** 裁剪最小尺寸 */
  const minAreaSize = 60;

  // 裁剪容器
  const targetRef = useRef<HTMLDivElement>(null);
  const targetPosition = useRef<DOMRect>(null);
  useMount(() => {
    if (targetRef.current) {
      const rect = targetRef.current?.getBoundingClientRect();
      const { width: rawWidth, height: rawHeight } = rect;
      const width = Math.min(rawWidth, 500);
      const height = Math.min(rawHeight, 500);

      // 设置默认框选范围
      setCoordinate(
        defaultCoordinate || {
          top: 10,
          left: 10,
          bottom: Math.max(height, minAreaSize) - 10,
          right: width - 10,
        }
      );
    }
  });

  const moveActionRef = useRef<MoveAction>(null);
  // 开始移动时初始位置
  const movePrevPositionRef = useRef<{ x: number; y: number }>(null);
  const prevCoordinateRef = useRef<IRectCoordinate>(null);

  const onStartMove = useMemoizedFn((e: TPointerEvent) => {
    const target = e.target as HTMLElement;

    // 如果触发对象不在容器nz-crop内，则不触发移动
    if (targetRef.current && !targetRef.current.contains(target)) {
      return;
    }

    const rect = targetRef.current?.getBoundingClientRect();
    // 设置容器位置
    targetPosition.current = rect;

    // 根据当前元素的宽高设置边框限制
    edgeLimitCoordinate.current = {
      top: 10,
      left: 10,
      bottom: rect.height - 10,
      right: rect.width - 10,
    };

    if (target) {
      const moveAction =
        target.getAttribute("data-move-action") ||
        getParentAttribute(target, "data-move-action");

      if (moveAction !== null) {
        //解决拖动会选中文字的问题
        e.preventDefault();
        //设置当前动作与方向
        moveActionRef.current = +moveAction;

        // 整个移动时，需要记录触发位置，来计算位移距离
        if (
          moveActionRef.current === MoveAction.whole ||
          moveActionRef.current === MoveAction.resize
        ) {
          let { clientX: x, clientY: y } =
            e instanceof TouchEvent ? e.touches[0] : e;
          movePrevPositionRef.current = { x, y };
        } else {
          prevCoordinateRef.current = coordinate;
        }
      }
    }

    return false;
  });

  // 整体移动
  const onWholeMoving = useMemoizedFn((e: TPointerEvent) => {
    const { x, y } = movePrevPositionRef.current;
    let { clientX, clientY } = e instanceof TouchEvent ? e.touches[0] : e;
    const offsetX = clientX - x;
    const offsetY = clientY - y;

    setCoordinate((prev) => {
      const next = {
        left: prev.left + offsetX,
        top: prev.top + offsetY,
        right: prev.right + offsetX,
        bottom: prev.bottom + offsetY,
      };

      const limit = edgeLimitCoordinate.current;

      // 如果超出边界，则同方向上不变
      if (limit.left > next.left || next.right > limit.right) {
        next.left = prev.left;
        next.right = prev.right;
      }

      if (limit.top > next.top || next.bottom > limit.bottom) {
        next.top = prev.top;
        next.bottom = prev.bottom;
      }

      onResizing?.(next);
      movePrevPositionRef.current = { x: clientX, y: clientY };

      return next;
    });
  });

  const onReset = useMemoizedFn(() => {
    setCoordinate(defaultCoordinate);
  });

  // 拖拽边界移动
  const onEdgeMoving = useMemoizedFn((e: TPointerEvent) => {
    /**当前动作 */
    const moveAction = moveActionRef.current;
    /**当前裁剪内容元素位置 */
    const position = targetPosition.current;
    const preCoordinate = prevCoordinateRef.current;

    let { clientX, clientY } = e instanceof TouchEvent ? e.touches[0] : e;
    // 获取位移之后的位置
    let top = clientY - position.y;
    let left = clientX - position.x;
    let right = clientX - position.x;
    let bottom = clientY - position.y;

    let nextCoordinate = null;

    // 处理各个移动方向的坐标位置
    switch (moveAction) {
      case MoveAction.top:
        nextCoordinate = {
          top: Math.min(preCoordinate.bottom - minAreaSize, top),
          bottom: Math.max(top, preCoordinate.bottom),
        };
        break;
      case MoveAction.right:
        nextCoordinate = {
          right: Math.max(right, preCoordinate.left + minAreaSize),
          left: Math.min(preCoordinate.left, right),
        };
        break;
      case MoveAction.bottom:
        nextCoordinate = {
          bottom: Math.max(bottom, preCoordinate.top + minAreaSize),
          top: Math.min(preCoordinate.top, bottom),
        };
        break;
      case MoveAction.left:
        nextCoordinate = {
          left: Math.min(preCoordinate.right - minAreaSize, left),
          right: Math.max(left, preCoordinate.right),
        };
        break;
      case MoveAction.topLeft:
        nextCoordinate = {
          top: Math.min(preCoordinate.bottom - minAreaSize, top),
          bottom: Math.max(top, preCoordinate.bottom),
          left: Math.min(preCoordinate.right - minAreaSize, left),
          right: Math.max(left, preCoordinate.right),
        };
        break;
      case MoveAction.topRight:
        nextCoordinate = {
          top: Math.min(preCoordinate.bottom - minAreaSize, top),
          bottom: Math.max(top, preCoordinate.bottom),
          right: Math.max(right, preCoordinate.left + minAreaSize),
          left: Math.min(preCoordinate.left, right),
        };
        break;
      case MoveAction.bottomRight:
        nextCoordinate = {
          bottom: Math.max(bottom, preCoordinate.top + minAreaSize),
          top: Math.min(preCoordinate.top, bottom),
          right: Math.max(right, preCoordinate.left + minAreaSize),
          left: Math.min(preCoordinate.left, right),
        };
        break;
      case MoveAction.bottomLeft:
        nextCoordinate = {
          bottom: Math.max(bottom, preCoordinate.top + minAreaSize),
          top: Math.min(preCoordinate.top, bottom),
          left: Math.min(preCoordinate.right - minAreaSize, left),
          right: Math.max(left, preCoordinate.right),
        };
        break;
      default:
        return;
    }

    setCoordinate((prev) => {
      let next = { ...prev, ...nextCoordinate };

      // 限制尺寸范围
      next = applyBoundaryConstraints(next, {
        ...edgeLimitCoordinate.current,
        size: minAreaSize,
      });

      // resize回调
      onResizing?.(next);

      return next;
    });
  });

  const onMoving = useMemoizedFn((e: TPointerEvent) => {
    const moveAction = moveActionRef.current;

    if (moveAction === null) {
      return;
    }

    if (moveAction === MoveAction.whole) {
      onWholeMoving(e);
    } else if (moveAction === MoveAction.resize) {
      onReSelection(e);
    } else {
      onEdgeMoving(e);
    }
  });

  const onMoveEnd = useMemoizedFn(() => {
    const currentAction = moveActionRef.current;
    if (currentAction !== null) {
      // 清空动作
      moveActionRef.current = null;

      // 重新框选尺寸时，结束应用框选尺寸范围
      if (currentAction === MoveAction.resize) {
        if (resizeCoordinate === null) {
          onReset();
        } else {
          const nextCoordinate = applyBoundaryConstraints(resizeCoordinate, {
            ...edgeLimitCoordinate.current,
            size: minAreaSize,
          });
          setCoordinate(nextCoordinate);
          // onResizeEnd回调
          onResizeEnd?.(nextCoordinate);
          setResizeCoordinate(null);
        }
      }

      // onResizeEnd回调
      onResizeEnd?.(coordinate);
    }
  });

  // 重新框选范围坐标值
  const [resizeCoordinate, setResizeCoordinate] =
    useState<IRectCoordinate>(null);
  //重新框选范围
  const onReSelection = useMemoizedFn((e: TPointerEvent) => {
    const startPosition = movePrevPositionRef.current as {
      x: number;
      y: number;
    };
    /**当前裁剪内容元素位置 */
    const position = targetPosition.current;
    let { clientX, clientY } = e instanceof TouchEvent ? e.touches[0] : e;

    setResizeCoordinate({
      top: Math.min(startPosition.y - position.y, clientY - position.y),
      bottom: Math.max(startPosition.y - position.y, clientY - position.y),
      left: Math.min(startPosition.x - position.x, clientX - position.x),
      right: Math.max(startPosition.x - position.x, clientX - position.x),
    });
  });

  // 监听鼠标|触摸事件
  useEventListener(EVENT_POINTER_DOWN, onStartMove);
  useEventListener(EVENT_POINTER_UP, onMoveEnd);
  useEventListener(EVENT_POINTER_CANCEL, onMoveEnd);
  useEventListener("blur", onMoveEnd);
  useEventListener(EVENT_POINTER_MOVE, onMoving);

  return {
    targetRef,
    coordinate,
    resizeCoordinate,
    setCoordinate,
  };
}

type TypeLimit = IRectCoordinate & { size: number };

/**
 * 应用尺寸与边界限制
 * @param coordinate
 * @param limit
 * @returns
 */
function applyBoundaryConstraints(
  coordinate: IRectCoordinate,
  limit: TypeLimit
) {
  const { left, top, right, bottom } = coordinate;
  const next = { ...coordinate };

  const isMinSize: { vertical?: boolean; horizontal?: boolean } = {};

  // 左右尺寸限制
  if (right - left <= limit.size) {
    next.right = left + limit.size;
    isMinSize.horizontal = true;
  }
  // 上下尺寸限制
  if (bottom - top <= limit.size) {
    next.bottom = top + limit.size;
    isMinSize.vertical = true;
  }

  // 边界限制
  if (next.left < limit.left) {
    next.left = limit.left;
    // 如果当前横向已经是最小尺寸，则两边同时移动
    if (isMinSize.horizontal) {
      next.right = next.left + limit.size;
    }
  }

  if (next.right > limit.right) {
    next.right = limit.right;
    // 如果当前横向已经是最小尺寸，则两边同时移动
    if (isMinSize.horizontal) {
      next.left = next.right - limit.size;
    }
  }

  if (next.top < limit.top) {
    next.top = limit.top;
    // 如果当前纵向已经是最小尺寸，则两边同时移动
    if (isMinSize.vertical) {
      next.bottom = next.top + limit.size;
    }
  }

  if (next.bottom > limit.bottom) {
    next.bottom = limit.bottom;
    // 如果当前纵向已经是最小尺寸，则两边同时移动
    if (isMinSize.vertical) {
      next.top = next.bottom - limit.size;
    }
  }

  return next;
}
